import { NavigationTrackableItem } from '@/shared/tracking/gtm.model';
import { NavigationItemModel, LinkType } from '@/types/B2CcontentServerContract';
import useNavigationRenderer from '@/shared/navigation/site-navigation/useNavigationRenderer';
import authStore from '@/shared/auth/auth.store';
import gtm from '@/shared/tracking/gtm.service';
import navigationStore from '@/shared/navigation/navigation.store';

const { getIsSelectedItem, getSelectedItemByIndex } = useNavigationRenderer();

class NavigationTrackingService {
    public logoutLink(): void {
        const parentId = authStore.isSignedInAsPartner ? -300 : -100;
        const name = 'Logout';
        const logoutNavigationItemMock = {
            linkType: LinkType.ItemWithOnlyLink,
            name: name,
            parentId: parentId,
            path: ['My Bolia', 'My Bolia', name],
        };
        this.menuItem(logoutNavigationItemMock);
    }

    public myBoliaProfileLinks(item: {name: string }): void {
        const profileLinkItemMock = {
            name: item.name,
            parentId: -110,
            linkType: LinkType.ItemWithOnlyLink,
            path: ['My Bolia', 'My Bolia', item.name],
        };
        this.menuItem(profileLinkItemMock);
    }

    public searchLink(): void {
        const name = 'Search suggest';
        const searchNavigationItemMock = {
            name: name,
            path: [name],
            linkType: LinkType.ItemWithOnlyLink,
            parentId: 0,
        };
        this.menuItem(searchNavigationItemMock);
    }

    public menuItem(item: NavigationTrackableItem | null): void {
        const depthIndex: number = this.getDepthIndex(item);
        const levelOneName = this.getNameForIndex(item, 0);
        const levelThreeName = this.getNameForIndex(item, 2);
        const levelTwoName = this.getNameForIndex(item, 1);

        const trackingData = {
            action: `${levelOneName}_lvl${depthIndex}`,
            category: 'Megamenu navigation',
            label: levelThreeName ? `${levelTwoName}/${levelThreeName}` : `${levelTwoName}`,
            value: item ? this.getInteractionType(item, depthIndex) : 'close',
        };
        gtm.megaMenu(trackingData);
    }

    private getNameForIndex(item: NavigationTrackableItem | null, depthIndex: number): string {
        if (!!item && !!item.path[depthIndex]) {
            return item.path[depthIndex];
        }
        const activeMainItem = this.getActiveMainmenu();
        const activeSubItem = this.getActiveSubmenu();

        switch (depthIndex) {
        case 0:
            return activeMainItem ? activeMainItem.name : '';
        case 1:
            return activeSubItem ? activeSubItem.name : '';
        default: return '';
        }
    }

    private getInteractionType(item: NavigationTrackableItem, depthIndex: number): string {
        const isActiveMainItem = this.getActiveMainmenu() ? this.getActiveMainmenu()!.id === item.id : false;
        const isActiveSubItem = this.getActiveSubmenu() ? this.getActiveSubmenu()!.id === item.id : false;
        const isActiveMyBoliaItem = item ? getIsSelectedItem(item as any, depthIndex - 1) : false;

        const isLink = item.linkType !== LinkType.None;
        const isClose = !isLink && item.path.length === 1 && !isActiveMainItem;
        const isActive = (isActiveMainItem && depthIndex === 0) || (isActiveSubItem && depthIndex === 1) || isActiveMyBoliaItem;
        const isNoLongerActive = ((!isActiveMainItem && depthIndex === 0) || (!isActiveSubItem && depthIndex === 1));

        if (isClose) {
            return 'close';
        } else if (isLink) {
            return `navigate - ${this.getNavigatePostFix(item)}`;
        } else if (isActive) {
            return 'open';
        } else if (isNoLongerActive) {
            return 'collapse';
        }

        return '';
    };

    private getNavigatePostFix(item: NavigationTrackableItem): string {
        return item.linkType === LinkType.ItemWithOnlyLink ? 'item link' : 'content link';
    }

    private getDepthIndex(item: NavigationTrackableItem | null): number {
        if (item) {
            return item.path.length - 1;
        }
        return this.getActiveSubmenu() ? 1 : 0;
    };

    private getActiveMainmenu(): NavigationItemModel | null | undefined {
        return navigationStore.isMobileNavigation ? getSelectedItemByIndex(0) : navigationStore.activeMainNavigationItem;
    }

    private getActiveSubmenu(): NavigationItemModel | null | undefined {
        return navigationStore.isMobileNavigation ? getSelectedItemByIndex(1) : navigationStore.activeSubNavigationItem;
    }
}

export default new NavigationTrackingService();
